const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return undefined;
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
};

const GRAMS_PER_KWH_TO_LBS_PER_MWH = 0.00220462 * 1000;
const DEFAULT_ELECTRIC_EMISSIONS_G_PER_KWH = 408.23;

// Turns the /location API response into user preference values. Fields that
// are missing from the response are left out so the existing preference (or
// preset) is kept, instead of being overwritten with NaN or throwing.
export const parseLocationPrefs = (location, { defaultSalesTax } = {}) => {
  const financial = location?.regional_financial_references?.[0];
  const fuelCost = location?.regional_fuel_cost?.[0];
  const prefs = {};

  const sofr = toFiniteNumber(
    location?.national_financial_references?.[0]?.rates?.sofr
  );
  if (sofr !== undefined) prefs.interestRateAsBasisPoints = sofr * 100;

  const salesTax = toFiniteNumber(financial?.sales_tax?.combined);
  if (salesTax !== undefined) prefs.salesTax = salesTax;
  else if (defaultSalesTax !== undefined) prefs.salesTax = defaultSalesTax;

  const electricity = toFiniteNumber(fuelCost?.electricity);
  if (electricity !== undefined) {
    prefs.electricityRate = Number(electricity.toFixed(4));
  }

  const gasoline = toFiniteNumber(fuelCost?.gasoline);
  if (gasoline !== undefined) prefs.gasolinePriceInCentsPerGal = gasoline * 100;

  // Key name is misspelled throughout the app; keep it for compatibility.
  prefs.evRegistraionFeesRegion =
    toFiniteNumber(financial?.ev_registration_fees?.region) ?? 0;

  const gasolineEmissions = toFiniteNumber(
    location?.national_fuel_emissions?.[1]?.value
  );
  if (gasolineEmissions !== undefined) {
    prefs.gasolineEmissions = gasolineEmissions * 100;
  }

  const electricEmissions =
    toFiniteNumber(location?.regional_electricity?.[0]?.emissions?.emissions_co2) ||
    DEFAULT_ELECTRIC_EMISSIONS_G_PER_KWH;
  prefs.electricEmissions = electricEmissions * GRAMS_PER_KWH_TO_LBS_PER_MWH;

  return prefs;
};

export const prefsChanged = (prevPrefs, nextPrefs, keys) =>
  keys.some((key) => prevPrefs[key] !== nextPrefs[key]);

// Attaches dealer type info to each dealer. A dealer type matches on
// (dealer id OR dealership handle) AND oem; a dealer can match several types,
// in which case it is repeated once per type. Unmatched dealers pass through.
export const enhanceDealers = (dealers, dealerTypes) => {
  if (!Array.isArray(dealers)) return [];
  if (!Array.isArray(dealerTypes) || dealerTypes.length === 0) return dealers;

  const byId = new Map();
  const byHandle = new Map();
  const index = (map, key, i) => {
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(i);
  };

  dealerTypes.forEach((type, i) => {
    index(byId, `${type.dealerId}|${type.oem}`, i);
    index(byHandle, `${type.dealershipHandle}|${type.oem}`, i);
  });

  return dealers.flatMap((dealer) => {
    const matches = new Set([
      ...(byId.get(`${dealer.dealer_id}|${dealer.oem}`) || []),
      ...(byHandle.get(`${dealer.dealership_handle}|${dealer.oem}`) || []),
    ]);

    if (matches.size === 0) return [dealer];

    return [...matches]
      .sort((a, b) => a - b)
      .map((i) => ({
        ...dealer,
        type: dealerTypes[i].type,
        oem: dealerTypes[i].oem,
      }));
  });
};

export const getCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
};
